import React, { useContext, useEffect, useState } from "react";
import { AiVisibilityRoutes } from "../../Routes";
import { NavLink } from "reactstrap";
import { NavLink as NavLinkRRD, useLocation } from "react-router-dom";
import { ProductContext } from "../../store";
import Skeleton from "react-loading-skeleton";

function navItemClassName(active) {
	return `navItem${active ? " active" : ""}`;
}

function isPathMatch(pathname, fullPath) {
	return pathname === fullPath || pathname.startsWith(`${fullPath}/`);
}

function isAiGraderActive(pathname) {
	return (
		isPathMatch(pathname, "/aivisibility/audit-list") ||
		isPathMatch(pathname, "/aivisibility/report")
	);
}

function isRecommendationsActive(pathname) {
	return (
		isPathMatch(pathname, "/aivisibility/writing-recommendations") ||
		isPathMatch(pathname, "/aivisibility/recommendations")
	);
}

function isFlatRouteActive(route, pathname) {
	if (route.path === "/audit-list") {
		return isAiGraderActive(pathname);
	}
	if (route.path === "/writing-recommendations") {
		return isRecommendationsActive(pathname);
	}
	const fullPath = route.layout + route.path;
	return isPathMatch(pathname, fullPath);
}

const AiVisibilitySidebar = () => {
	const [subMenuOpen, setSubMenuOpen] = useState(null);
	const [websitePlatform, setWebsitePlatform] = useState("");
	const location = useLocation();
	const {
		addPageOpen,
		userLoader,
		websiteData,
		currentWebsiteId,
		userWebsitePlan,
		productTypes,
		monitoringEnabled,
	} = useContext(ProductContext);

	useEffect(() => {
		if (userLoader || !userWebsitePlan || !Array.isArray(userWebsitePlan) || userWebsitePlan.length === 0) {
			return;
		}
		if (currentWebsiteId) {
			setWebsitePlatform(websiteData[currentWebsiteId]?.websiteType);
		}
	}, [currentWebsiteId, userLoader, userWebsitePlan, websiteData]);

	useEffect(() => {
		const determineOpenSubmenu = () => {
			const currentPath = location.pathname;
			let openMenu = null;

			AiVisibilityRoutes.forEach((route) => {
				if (route.subMenu && route.subMenu.length > 0) {
					// Hide Monitoring children until enabled — treat as flat item
					if (route.name === "Monitoring" && !monitoringEnabled) {
						return;
					}

					if (route.name === "Monitoring" && monitoringEnabled) {
						const monitoringBase = `${route.layout}/monitoring`;
						if (
							currentPath === monitoringBase ||
							currentPath.startsWith(`${monitoringBase}/`)
						) {
							openMenu = route.name;
						}
						return;
					}

					const hasMatchingSubmenu = route.subMenu.some((subMenu) => {
						if (!subMenu.path) return false;
						const fullPath = route.layout + subMenu.path;
						return currentPath === fullPath || currentPath.startsWith(fullPath + "/");
					});

					if (hasMatchingSubmenu) {
						openMenu = route.name;
					}
				}
			});

			// Keep Content Studio expanded on hidden preview / deploy routes
			if (
				currentPath === "/aivisibility/writing-assistance/view" ||
				currentPath.startsWith("/aivisibility/writing-assistance/view/") ||
				currentPath === "/aivisibility/writing-assistance/monitoring" ||
				currentPath.startsWith("/aivisibility/writing-assistance/monitoring/") ||
				currentPath === "/aivisibility/writing-assistance/settings" ||
				currentPath.startsWith("/aivisibility/writing-assistance/settings/") ||
				currentPath === "/aivisibility/deployments/view" ||
				currentPath.startsWith("/aivisibility/deployments/view/") ||
				currentPath === "/aivisibility/dual-lens/view" ||
				currentPath.startsWith("/aivisibility/dual-lens/view/") ||
				currentPath === "/aivisibility/dual-lens" ||
				currentPath.startsWith("/aivisibility/dual-lens/") ||
				currentPath === "/aivisibility/llms-txt/view" ||
				currentPath.startsWith("/aivisibility/llms-txt/view/") ||
				currentPath === "/aivisibility/llms-txt" ||
				currentPath.startsWith("/aivisibility/llms-txt/")
			) {
				openMenu = "Content Studio";
			}

			setSubMenuOpen(openMenu);
		};

		determineOpenSubmenu();
	}, [location.pathname, monitoringEnabled]);

	const isRouteSubMenuActive = (route, pathname) => {
		if (route.name === "Monitoring" && route.path) {
			const base = route.layout + route.path;
			if (pathname === base || pathname.startsWith(`${base}/`)) return true;
		}
		if (route.name === "Content Studio") {
			if (
				pathname === "/aivisibility/writing-assistance/view" ||
				pathname.startsWith("/aivisibility/writing-assistance/view/") ||
				pathname === "/aivisibility/writing-assistance/monitoring" ||
				pathname.startsWith("/aivisibility/writing-assistance/monitoring/") ||
				pathname === "/aivisibility/writing-assistance/settings" ||
				pathname.startsWith("/aivisibility/writing-assistance/settings/") ||
				pathname === "/aivisibility/deployments" ||
				pathname.startsWith("/aivisibility/deployments/")
			) {
				return true;
			}
		}
		return route.subMenu?.some((subMenu) => {
			if (!subMenu.path) return false;
			const fullPath = route.layout + subMenu.path;
			// Fix from Audit must not steal /writing-assistance/view
			if (subMenu.path === "/writing-assistance") {
				return pathname === fullPath;
			}
			if (subMenu.path === "/writing-assistance/monitoring") {
				return (
					pathname === fullPath ||
					pathname.startsWith(`${fullPath}/`)
				);
			}
			if (subMenu.path === "/deployments") {
				return (
					pathname === fullPath ||
					pathname.startsWith(`${fullPath}/`)
				);
			}
			return pathname === fullPath || pathname.startsWith(fullPath + "/");
		});
	};

	const isContentStudioSubActive = (subMenu, pathname) => {
		const fullPath = subMenu.layout + subMenu.path;
		if (subMenu.path === "/writing-assistance") {
			return pathname === fullPath;
		}
		if (subMenu.path === "/writing-assistance/monitoring") {
			return isPathMatch(pathname, fullPath);
		}
		if (subMenu.path === "/writing-assistance/settings") {
			return isPathMatch(pathname, fullPath);
		}
		if (subMenu.path === "/deployments") {
			return isPathMatch(pathname, fullPath);
		}
		return isPathMatch(pathname, fullPath);
	};

	const isMonitoringSubActive = (subMenu, pathname) => {
		const fullPath = subMenu.layout + subMenu.path;
		return pathname === fullPath;
	};

	const resolveSubMenuActive = (route, subMenu, pathname) => {
		if (route.name === "Content Studio") {
			return isContentStudioSubActive(subMenu, pathname);
		}
		if (route.name === "Monitoring") {
			return isMonitoringSubActive(subMenu, pathname);
		}
		return isPathMatch(pathname, subMenu.layout + subMenu.path);
	};

	const getSectionBase = (route) => {
		if (route.name === "Monitoring") {
			return `${route.layout}/monitoring`;
		}
		return route.path ? route.layout + route.path : null;
	};

	const handleTopMenu = (e, route) => {
		const sectionBase = getSectionBase(route);
		const onSection =
			sectionBase &&
			(location.pathname === sectionBase ||
				location.pathname.startsWith(`${sectionBase}/`));

		// Monitoring (and similar nested sections): toggle in place instead of
		// always forcing open while already on a child route.
		if (onSection && route.subMenu?.length) {
			e.preventDefault();
			setSubMenuOpen((prev) => (prev === route.name ? null : route.name));
			return;
		}

		if (location.pathname && route.path && location.pathname !== route.layout + route.path) {
			setSubMenuOpen(route.name);
			return;
		}
		if (!route.path) {
			e.preventDefault();
		}
		setSubMenuOpen((prev) => (prev === route.name ? null : route.name));
	};

	return (
		<div className="innerSideBarContainer" style={{ display: addPageOpen ? "none" : "flex" }}>
			<header className="inner-header text-center align-items-center justify-content-start d-flex">
				<i className="fas fa-eye stl-text-grey-800" style={{ fontSize: "16px", marginLeft: "8px" }} />
				<span className="stl-text-grey-800" style={{ marginLeft: "8px", fontSize: "17px", fontWeight: "600" }}>
					AI Visibility
				</span>
			</header>
			{websitePlatform === "" ? (
				<>
					<Skeleton height={10} />
					<Skeleton height={10} />
				</>
			) : (
				<nav aria-label="AI Visibility Navigation">
					<ul className="searchNavigationList">
						{AiVisibilityRoutes.map((route, index) => {
							if (
								!productTypes?.includes("aivisibility") ||
								route.hidden ||
								(route.availablePlatforms && !route.availablePlatforms.includes(websitePlatform))
							) {
								return null;
							}

							const showAsFlatMonitoring =
								route.name === "Monitoring" && !monitoringEnabled;
							const monitoringTopTo =
								route.name === "Monitoring" && monitoringEnabled
									? `${route.layout}/monitoring/overview`
									: route.path
										? route.layout + route.path
										: "#";

							if (route.subMenu && route.subMenu.length > 0 && !showAsFlatMonitoring) {
								return (
									<li key={index}>
										<NavLink
											to={monitoringTopTo}
											tag={NavLinkRRD}
											className={navItemClassName(
												isRouteSubMenuActive(route, location.pathname)
											)}
											onClick={(e) => handleTopMenu(e, route)}
										>
											<div className="d-flex align-items-center justify-content-between">
												<div className="iconStringWrap">
													<i
														className={`fas ${route.fontIcon} text-white`}
														style={{ fontSize: "14px", marginRight: "8px" }}
													/>
													{route.name}
												</div>
												<i
													className={`fas ${subMenuOpen === route.name ? "fa-chevron-down" : "fa-chevron-right"}`}
													style={{ fontSize: "10px" }}
												/>
											</div>
										</NavLink>
										{subMenuOpen === route.name && (
											<div id={`linkGroup${index + 1}`} className="linkGroup">
												<ul>
													{route.subMenu.map((subMenu, subIndex) => {
														if (
															subMenu.menuFlag &&
															(!subMenu.availablePlatforms ||
																subMenu.availablePlatforms.includes(websitePlatform))
														) {
															if (subMenu.comingSoon) {
																return (
																	<li key={subIndex}>
																		<span
																			className="navItem navItem--disabled content-studio-nav-item--soon"
																			aria-disabled="true"
																		>
																			<span>{subMenu.name}</span>
																			<span className="content-studio-soon-badge">
																				Soon
																			</span>
																		</span>
																	</li>
																);
															}
															const subMenuActive = resolveSubMenuActive(
																route,
																subMenu,
																location.pathname
															);
															return (
																<li key={subIndex}>
																	<NavLink
																		to={subMenu.layout + subMenu.path}
																		tag={NavLinkRRD}
																		end={subMenu.path === "/writing-assistance"}
																		className={navItemClassName(subMenuActive)}
																		onClick={(e) => e.stopPropagation()}
																	>
																		{subMenu.name}
																	</NavLink>
																</li>
															);
														}
														return null;
													})}
												</ul>
											</div>
										)}
									</li>
								);
							}

							if (showAsFlatMonitoring) {
								return (
									<li key={index}>
										<NavLink
											to={route.layout + route.path}
											tag={NavLinkRRD}
											className={navItemClassName(
												isFlatRouteActive(route, location.pathname)
											)}
										>
											<div className="iconStringWrap">
												<i
													className={`fas ${route.fontIcon} text-white`}
													style={{ fontSize: "14px", marginRight: "8px" }}
												/>
												{route.name}
											</div>
										</NavLink>
									</li>
								);
							}

							return (
								<li key={index} className="sidebar-nav-item--flat">
									<NavLink
										to={route.layout + route.path}
										tag={NavLinkRRD}
										className={navItemClassName(
											isFlatRouteActive(route, location.pathname)
										)}
									>
										<div className="iconStringWrap">
											<i
												className={`fas ${route.fontIcon} text-white`}
												style={{ fontSize: "14px", marginRight: "8px" }}
											/>
											{route.name}
										</div>
									</NavLink>
								</li>
							);
						})}
					</ul>
				</nav>
			)}
		</div>
	);
};

export default AiVisibilitySidebar;
